import React, { useState, useEffect } from 'react';
import { CreditCard, AlertTriangle, CheckCircle, XCircle, Ban, Loader2 } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { useServices, useToast, getErrorMessage } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import type { RfidCredential, RfidCredentialStatus, RfidCardType } from '@/shared/types';

// Helper to mask tag ID
const maskTagId = (tagId: string) => {
  if (tagId.length <= 4) return tagId;
  return '****' + tagId.slice(-4);
};

// Card type labels
const CARD_TYPE_LABELS: Record<RfidCardType, string> = {
  PROXIMITY: 'Proximidad',
  MIFARE: 'MIFARE',
  HID: 'HID',
  EM4100: 'EM4100',
  OTHER: 'Otro',
};

// Status badge colors
const getStatusInfo = (status: RfidCredentialStatus) => {
  switch (status) {
    case 'ACTIVE':
      return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle, label: 'Activa' };
    case 'INACTIVE':
      return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', icon: XCircle, label: 'Inactiva' };
    case 'LOST':
      return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'Perdida' };
    case 'REVOKED':
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: Ban, label: 'Revocada' };
    default:
      return { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', icon: XCircle, label: status };
  }
};

export const RfidCardSection: React.FC = () => {
  const { t } = useI18n();
  const toast = useToast();
  const { formatDate } = useDateFormatter();
  const { rfidCredentialRepo } = useServices();
  const [cards, setCards] = useState<RfidCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [reportingId, setReportingId] = useState<string | null>(null);

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    setLoading(true);
    try {
      const myCards = await rfidCredentialRepo.getMyCredentials();
      setCards(myCards);
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al cargar tarjetas');
    } finally {
      setLoading(false);
    }
  };

  const handleReportLost = async (id: string) => {
    setReportingId(id);
    try {
      await rfidCredentialRepo.reportLost(id);
      toast.success('Tarjeta reportada como perdida. Un administrador ha sido notificado.');
      await loadCards();
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al reportar tarjeta');
    } finally {
      setReportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
        <CreditCard size={48} className="mx-auto text-slate-300 mb-3" />
        <h4 className="font-bold text-slate-600 mb-1">Sin tarjetas RFID</h4>
        <p className="text-sm text-slate-500">No tienes tarjetas de acceso asignadas.</p>
        <p className="text-xs text-slate-400 mt-2">Contacta a administración para solicitar una tarjeta.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in">
      <p className="text-slate-500 text-sm">
        Tus tarjetas de acceso RFID para puertas y torniquetes.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => {
          const status = getStatusInfo(card.status);
          const StatusIcon = status.icon;
          const isReporting = reportingId === card.id;
          const canReportLost = card.status === 'ACTIVE' || card.status === 'INACTIVE';

          return (
            <div
              key={card.id}
              className={`bg-white p-5 rounded-xl border shadow-sm transition-all hover:shadow-md ${
                card.status === 'ACTIVE' ? 'border-green-200' : 'border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${card.status === 'ACTIVE' ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-500'}`}>
                  <CreditCard size={24} />
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${status.bg} ${status.text} ${status.border}`}>
                  <StatusIcon size={12} />
                  {status.label}
                </span>
              </div>

              <div className="mb-4">
                <h4 className="text-2xl font-mono font-bold text-slate-800 tracking-wider mb-1">
                  {maskTagId(card.tagId)}
                </h4>
                <p className="text-sm text-slate-600 font-medium">{card.label}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {CARD_TYPE_LABELS[card.cardType] || card.cardType}
                </p>
              </div>

              <div className="text-xs text-slate-400 mb-4">
                Asignada el {formatDate(card.createdAt)}
              </div>

              {canReportLost && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full text-amber-600 hover:bg-amber-50 border-amber-200"
                  onClick={() => handleReportLost(card.id)}
                  isLoading={isReporting}
                  icon={<AlertTriangle size={14} />}
                >
                  Reportar Perdida
                </Button>
              )}

              {card.status === 'LOST' && (
                <div className="text-center text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                  Esta tarjeta ha sido bloqueada. Contacta administración para un reemplazo.
                </div>
              )}

              {card.status === 'REVOKED' && (
                <div className="text-center text-xs text-red-600 bg-red-50 p-2 rounded-lg">
                  Esta tarjeta ha sido revocada por un administrador.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
